import { QueryClient } from '@tanstack/react-query';
import { skillBucketApi } from './api';

export interface SkillGroup {
  name: string;
  skills: string[];
}

export interface SkillRepository {
  createBucket(name: string): string | Promise<string>;
  deleteBucket(bucketId: string): void | Promise<void>;
  renameBucket(bucketId: string, name: string): void | Promise<void>;
  reorderBuckets(orderedBucketIds: string[]): void | Promise<void>;
  onSkillMoved(skill: string, fromBucketId: string, toBucketId: string): void;
  onSkillsReordered(bucketId: string, newOrder: string[]): void;
}

export function createApiSkillRepository(queryClient: QueryClient): SkillRepository {
  const invalidateSkillBuckets = () => {
    void queryClient.invalidateQueries({ queryKey: ['skillBuckets'] });
  };

  return {
    async createBucket(name) {
      const bucket = await skillBucketApi.create({ name });
      invalidateSkillBuckets();
      return bucket.id;
    },

    async deleteBucket(bucketId) {
      await skillBucketApi.delete(bucketId);
      invalidateSkillBuckets();
    },

    async renameBucket(bucketId, name) {
      await skillBucketApi.update(bucketId, { name });
      invalidateSkillBuckets();
    },

    async reorderBuckets(orderedBucketIds) {
      const reorders = orderedBucketIds.map((id, index) => ({
        id,
        sortOrder: index,
      }));
      await skillBucketApi.reorder(reorders);
      invalidateSkillBuckets();
    },

    onSkillMoved(skill, _fromBucketId, toBucketId) {
      if (toBucketId === 'other') {
        void skillBucketApi.unassignSkill(skill);
      } else {
        void skillBucketApi.assignSkill(toBucketId, skill);
      }
    },

    onSkillsReordered(bucketId, newOrder) {
      if (bucketId !== 'other') {
        void skillBucketApi.reorderSkills(bucketId, newOrder);
      }
    },
  };
}

export function createLocalSkillRepository(): SkillRepository {
  return {
    createBucket(name) {
      // Return a temporary local UUID for the new bucket
      return `local-${crypto.randomUUID()}`;
    },

    deleteBucket() {
      // No-op — deletion is handled by parent component state
    },

    renameBucket() {
      // No-op — renaming is handled by parent component state
    },

    reorderBuckets() {
      // No-op — reordering is handled by parent component state
    },

    onSkillMoved() {
      // No-op — skill moves are handled by drag-drop state
    },

    onSkillsReordered() {
      // No-op — reorders are handled by drag-drop state
    },
  };
}

export function getSkillGroups(
  items: Record<string, string[]>,
  buckets: Array<{ id: string; name: string }>
): SkillGroup[] {
  return buckets
    .filter((b) => b.id !== 'other')
    .map((b) => ({
      name: b.name,
      skills: items[b.id] ?? [],
    }));
}
